import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import delve from 'dlv';
import cloneDeep from 'clone-deep';
import { connect } from 'react-redux';
import { useDebouncedCallback } from 'use-debounce';
import PVExpire from '../ContentContainers/PVExpire';
import { addItem, updateQuantity, removeItem } from '../../../redux/cart/actionsAsync';
import { selectIsStopWebOrders } from '../../../redux/userProfile/selectors';
import PVQuantityChangedModal from '../../PVWebstore/PVQuantityChangedModal';
import PVUpdateCartErrorModal from './PVUpdateCartErrorModal';
import { actions as stepActions } from '../../../redux/checkout-steps/checkoutStepsSlice';
import PVAddToFavorite from './PVAddToFavorite';

const PVCartQuantitySpinner = ({
    itemId,
    initialValue,
    useAddToCartBtn,
    useAddToFavoritesBtn,
    parentName,
    apiAddToCart,
    apiUpdateQty,
    apiRemoveItem,
    enableControl,
    onSuccess,
    wrapperClass,
    dealerStopped,
    addToCartText,
    showQuantity,
    allowQtyZero,
    showStoppedModal,
    favoriteListId,
    itemMeetsPurchaseRequirements
}) => {
    const initialState = {
        isError: false,
        isUpdating: false,
        showSuccess: false,
        errors: [],
        qty: 1,
    };
    const [ctrlStatus, setCtrlStatus] = useState(initialState);
    const [quantityDialog, setQuantityDialog] = useState({ show: false, message: '' });
    const [oldValue, setOldValue] = useState(0);
    const [spinnerHasBeenClicked, setSpinnerHasBeenClicked] = useState(false);
    const isMounted = useRef(undefined);

    const changeStatus = (statusUpdates) => {
        if (isMounted.current) {
            const statusClone = cloneDeep(ctrlStatus);
            const newStatus = { ...statusClone, ...statusUpdates };
            setCtrlStatus(newStatus);
        }
    };

    useEffect(() => {
        isMounted.current = true;
        return () => {
            isMounted.current = false;
        };
    }, []);

    useEffect(() => {
        changeStatus({ qty: initialValue });
        setOldValue(initialValue);
    }, [initialValue]);

    useEffect(() => {
        if (
            itemId === '' &&
            !ctrlStatus.isError &&
            !ctrlStatus.isUpdating &&
            !quantityDialog.show
        ) {
            changeStatus(initialState);
        }
    }, [itemId, ctrlStatus.isError, ctrlStatus.isUpdating, ctrlStatus.lastId, quantityDialog.show]);

    const actions = {
        INCREMENT: 'increment',
        DECREMENT: 'decrement',
        EDIT: 'edit',
        UPDATE: 'update',
    };
    const testForInteger = (x) => {
        return Number.isInteger(Number(x));
    };

    const getItemQtyFromCartResults = (result) => {
        const carts = delve(result, 'payload.ShoppingCarts', []);
        const allItems = carts.reduce((acc, curr) => {
            const currItems = delve(curr, 'ShoppingCartItems', []);
            return [...acc, ...currItems];
        }, []);
        const item = allItems.find((x) => {
            const itemIdStr = (itemId || '').toString().toLowerCase().trim();
            const cartProductId = delve(x, 'Product.ID', '').toString().toLowerCase().trim();
            const cartItemId = (x.ItemID || '').toString().toLowerCase().trim();
            return cartItemId === itemIdStr || cartProductId === itemIdStr;
        });
        return delve(item, 'Quantity', null);
    };

    const [debouncePlusMinus] = useDebouncedCallback(
        (qty) => {
            // eslint-disable-next-line no-use-before-define
            handleQtyChange(actions.UPDATE, qty);
        },
        750,
        { maxWait: 5000 }
    );

    const handleQtyChange = (action, value = 1) => {
        if (!spinnerHasBeenClicked) {
            setSpinnerHasBeenClicked(true);
        };

        const doApiUpdate = !dealerStopped && (!useAddToCartBtn || action === actions.UPDATE);
        const debounceApiUpdate =
            !useAddToCartBtn && (action === actions.INCREMENT || action === actions.DECREMENT);

        const determineQuantity = new Promise((resolve, reject) => {
            let newVal = (ctrlStatus.qty || '').toString().trim();
            newVal = !Number.isNaN(newVal) ? Number(newVal) : 1;
            if (dealerStopped) {
                return reject();
            }
            if (!testForInteger(newVal) || (action === actions.EDIT && !testForInteger(value))) {
                return reject(new Error('Error: Value must be an integer'));
            }

            if (action === actions.INCREMENT) {
                newVal += value;
            } else if (action === actions.DECREMENT) {
                newVal -= value;
            } else if (action === actions.EDIT) {
                newVal = value;
            }

            if (allowQtyZero) {
                newVal = useAddToCartBtn && newVal < 0 ? 0 : newVal;
            } else {
                newVal = useAddToCartBtn && newVal <= 0 ? 1 : newVal;
            }

            return resolve(newVal);
        });

        const makeApiCall = (newVal) => {
            let apiCall;
            if (
                action === actions.UPDATE &&
                useAddToCartBtn &&
                !ctrlStatus.isUpdating &&
                !ctrlStatus.isError
            ) {
                apiCall = apiAddToCart(itemId, newVal);
            } else if (!ctrlStatus.isUpdating) {
                if (newVal > 0) {
                    apiCall = apiUpdateQty(itemId, newVal, parentName);
                } else {
                    apiCall = apiRemoveItem(itemId, `${parentName}-qty-0`);
                }
            }

            if (apiCall === undefined) {
                return Promise.reject(new Error('Unable to update cart'));
            }
            return apiCall;
        };
        let postApiQty = 1;
        determineQuantity
            .then((newVal) => {
                postApiQty = newVal;
                if (allowQtyZero && newVal === 0) {
                    return Promise.resolve();
                }

                if (debounceApiUpdate) {
                    debouncePlusMinus(newVal);
                } else {
                    changeStatus({
                        isUpdating: doApiUpdate,
                        isError: false,
                        errors: [],
                        qty: newVal,
                    });
                }
                if (doApiUpdate && !debounceApiUpdate) {
                    const apiResult = makeApiCall(newVal);
                    return apiResult;
                }
                return Promise.resolve();
            })
            .then((result) => {
                let apiUpdateSuccess = false;
                if (doApiUpdate && !debounceApiUpdate) {
                    if (result) {
                        const notifications = delve(result, 'payload.Notifications', []);
                        let qtyChanged = false;
                        if (notifications.length > 0) {
                            qtyChanged = notifications.find((x) => x.Title === 'Cart Change');
                        }

                        //Quantity changed (e.g. quantity increased due to minimum quantity rule)
                        if (qtyChanged && isMounted.current) {
                            setQuantityDialog({
                                show: true,
                                title: qtyChanged.Title,
                                message: qtyChanged.Description,
                            });

                            //If on a favorite list, don't change quantity.
                            if (!(favoriteListId > 0)) {
                                postApiQty = getItemQtyFromCartResults(result) || postApiQty;
                            }
                        } else if (favoriteListId > 0) {
                            //If on a favorites list, don't set quantity back to 1.
                        } else if (useAddToCartBtn) {
                            postApiQty = 1;
                        } else {
                            postApiQty = getItemQtyFromCartResults(result) || postApiQty;
                        }
                        apiUpdateSuccess = true;
                        onSuccess();
                    }
                }

                postApiQty = postApiQty < 0 ? 0 : postApiQty;

                changeStatus({
                    isUpdating: false,
                    showSuccess: apiUpdateSuccess,
                    qty: postApiQty,
                });
                if (action === actions.UPDATE) {
                    setOldValue(postApiQty);
                }
                return Promise.resolve();
            })
            .catch((err) => {
                if (dealerStopped) {
                    showStoppedModal();
                } else {
                    changeStatus({
                        errors: Array.isArray(err) ? err : [err],
                        showSuccess: false,
                        isError: true,
                        isUpdating: false,
                        qty: oldValue,
                    });
                }
            });
    };

    const [
        debouncedQtyOnChange,
        // eslint-disable-next-line no-unused-vars
        _cancel,
        pendingDebouncedChange,
    ] = useDebouncedCallback(handleQtyChange, 750, { maxWait: 1200 });

    const handleKeyPress = (e) => {
        if (useAddToCartBtn && e.key === 'Enter') {
            handleQtyChange(actions.UPDATE, ctrlStatus.qty);
        }
    };

    return (
        <React.Fragment>
            {ctrlStatus.isUpdating ? (
                <div className="pv-spinner xs" />
            ) : (
                <React.Fragment>
                    {quantityDialog.show && (
                        <PVQuantityChangedModal
                            message={quantityDialog.message}
                            title={quantityDialog.title}
                            isOpen={quantityDialog.show}
                            onRequestClose={() => setQuantityDialog({ show: false })}
                        />
                    )}
                    {!quantityDialog.show && ctrlStatus.isError && ctrlStatus.errors.length > 0 && (
                        <PVUpdateCartErrorModal
                            isOpen={ctrlStatus.isError}
                            onRequestClose={() => {
                                changeStatus({ isError: false, errors: [] });
                            }}
                            rawErrors={ctrlStatus.errors}
                            itemId={itemId}
                        />
                    )}
                    {itemId.length > 0 && (
                        <React.Fragment>
                                    {(itemMeetsPurchaseRequirements && showQuantity || favoriteListId > 0) && (
                                        <div
                                            className={`qty-spinner ${wrapperClass}`}
                                        >
                                            <div className="input-group number-spinner">
                                                <span className="input-group-btn">
                                                    <button
                                                        type="button"
                                                        className="pv btn btn-sm btn-default"
                                                        data-dir="dwn"
                                                        onClick={() => handleQtyChange(actions.DECREMENT)}
                                                    >
                                                        <span className="glyphicon glyphicon-minus" />
                                                    </button>
                                                </span>
                                                <input
                                                    type="number"
                                                    min={0}
                                                    className="pv form-control text-center"
                                                    id="txtQuantity"
                                                    data-itemid={itemId}
                                                    value={ctrlStatus.qty}
                                                    onChange={(e) => {
                                                        changeStatus({ qty: e.target.value });
                                                        debouncedQtyOnChange(actions.EDIT, e.target.value);
                                                    }}
                                                    onBlur={(e) =>
                                                        pendingDebouncedChange(actions.EDIT, e.target.value)
                                                    }
                                                    onKeyPress={handleKeyPress}
                                                    required
                                                    pattern="[0-9]{0,9}"
                                                />
                                                <span className="input-group-btn">
                                                    <button
                                                        type="button"
                                                        className="pv btn btn-sm btn-default"
                                                        data-dir="up"
                                                        onClick={() => handleQtyChange(actions.INCREMENT)}
                                                    >
                                                        <span className="glyphicon glyphicon-plus" />
                                                    </button>
                                                </span>
                                            </div>
                                        </div>
                                    )}
                                    {useAddToCartBtn && (
                                        <div className={`single-add-to-cart ${wrapperClass}`}>
                                            <button
                                                type="button"
                                                disabled={!enableControl}
                                                className={`pv btn btn-primary btn-add-to-cart ${
                                                    enableControl ? '' : ' disabled'
                                                }`}
                                                onClick={() =>
                                                    handleQtyChange(actions.UPDATE, ctrlStatus.qty)
                                                }
                                            >
                                                {addToCartText}
                                            </button>
                                        </div>
                                    )}
                            {useAddToFavoritesBtn && (
                                <PVAddToFavorite
                                    itemId={itemId}
                                    parentName={parentName}
                                    quantity={ctrlStatus.qty.toString()}
                                    spinnerHasBeenClicked={spinnerHasBeenClicked}
                                    favoriteListId={favoriteListId}
                                />
                            )}
                            {ctrlStatus.showSuccess && (
                                <PVExpire
                                    hidden={!ctrlStatus.showSuccess}
                                    expiresMs={3000}
                                    onComplete={() => {
                                        changeStatus({ showSuccess: false });
                                    }}
                                >
                                    <div className="well well-sm text-info bg-success added-to-cart">
                                        Cart updated!
                                    </div>
                                </PVExpire>
                            )}
                        </React.Fragment>
                    )}
                </React.Fragment>
            )}
        </React.Fragment>
    );
};

PVCartQuantitySpinner.propTypes = {
    itemId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    wrapperClass: PropTypes.string,
    initialValue: PropTypes.number,
    useAddToCartBtn: PropTypes.bool,
    useAddToFavoritesBtn: PropTypes.bool,
    parentName: PropTypes.string.isRequired,
    onSuccess: PropTypes.func,
    apiAddToCart: PropTypes.func,
    apiUpdateQty: PropTypes.func,
    apiRemoveItem: PropTypes.func,
    enableControl: PropTypes.bool,
    dealerStopped: PropTypes.bool.isRequired,
    addToCartText: PropTypes.string,
    showQuantity: PropTypes.bool,
    allowQtyZero: PropTypes.bool,
    showStoppedModal: PropTypes.func.isRequired,
    favoriteListId: PropTypes.string,
    itemMeetsPurchaseRequirements: PropTypes.bool.isRequired,
};

PVCartQuantitySpinner.defaultProps = {
    itemId: '',
    initialValue: 1,
    useAddToCartBtn: true,
    useAddToFavoritesBtn: false,
    enableControl: true,
    wrapperClass: '',
    addToCartText: 'Add to Cart',
    showQuantity: true,
    apiAddToCart: () => {},
    apiUpdateQty: () => {},
    apiRemoveItem: () => {},
    onSuccess: () => {},
    allowQtyZero: false,
    favoriteListID: 0,
};
const mapStateToProps = (state) => {
    return { dealerStopped: selectIsStopWebOrders(state) };
};
const mapDispatchToProps = (dispatch) => {
    return {
        apiAddToCart: (itemId, addQty) => dispatch(addItem(itemId, addQty, 'defaultAddToCart')),
        apiUpdateQty: (productId, updQty, controlName) =>
            dispatch(updateQuantity(productId, updQty, controlName)),
        apiRemoveItem: (productId, controlName) => dispatch(removeItem(productId, controlName)),
        showStoppedModal: () =>
            dispatch(stepActions.setAccountNotAllowed({ showAccountNotAllowed: true })),
    };
};

export default connect(mapStateToProps, mapDispatchToProps)(PVCartQuantitySpinner);
